import BatteryFullIcon from '@material-ui/icons/BatteryFull'
import EditIcon from '@material-ui/icons/Edit'
import LinkIcon from '@material-ui/icons/Link'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import PowerIcon from '@material-ui/icons/Power'
import RedoIcon from '@material-ui/icons/RedoOutlined'
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined'
import UndoIcon from '@material-ui/icons/UndoOutlined'
import { DownloadIcon, ExternalLinkIcon, SettingsIcon } from 'opensolar-ui'
import React from 'react'
import { getActionDescription, getActionTitle } from '../actionPermissions'
import { AdaAction } from '../actions'

export const getActionIcon = (actionTypeOrAction: string | AdaAction): React.ReactElement => {
  const iconSize = 20
  const iconColor = '#1751D0'

  const actionType = typeof actionTypeOrAction === 'string' ? actionTypeOrAction : actionTypeOrAction?.action || ''
  const action = typeof actionTypeOrAction === 'object' ? actionTypeOrAction : null

  switch (actionType) {
    case 'autoDesignEphemeral':
      return <SettingsIcon size={iconSize} color={iconColor} />
    case 'saveSystemDesignAsTemplate':
      return <SaveOutlinedIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'navigate':
      return <ExternalLinkIcon size={iconSize} color={iconColor} />
    case 'loadAutoDesignResult':
      return <DownloadIcon color={iconColor} />
    case 'loadInverterDesignAssistantResult':
      return <PowerIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'loadBatteryDesignAssistantResult':
      return <BatteryFullIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'runAutoString':
      return <LinkIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'runBatteryDesignAssistant':
      return <BatteryFullIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'runInverterDesignAssistant':
      return <PowerIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'systemEdit':
      return <EditIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    case 'undoRedo':
      if (action?.args?.direction === 'redo') {
        return <RedoIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
      }
      return <UndoIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
    default:
      return <PlayArrowIcon style={{ fontSize: `${iconSize}px`, color: iconColor }} />
  }
}

export const getActionTitleFromAction = (
  action: any,
  buttonLabel: string,
  translate: (key: string, options?: any) => string
): string => {
  const actionType = action?.action || ''

  // Special handling for undoRedo to show direction-specific title
  if (actionType === 'undoRedo') {
    const direction = action?.args?.direction
    if (direction === 'undo') {
      return translate('Undo last action')
    } else if (direction === 'redo') {
      return translate('Redo action')
    }
  }

  const title = getActionTitle(actionType)
  return title || buttonLabel || translate('Confirm Action')
}

export const buildActionDescription = (action: any, translate: (key: string, options?: any) => string): string => {
  const actionType = action?.action || ''
  const args = action?.args || {}

  let description =
    getActionDescription(actionType) ||
    (actionType === 'navigate'
      ? translate('This will navigate to %{destination}.', {
          destination: args?.pathname || args?.url || translate('a page'),
        })
      : translate('This action will be executed when you confirm.'))

  if (actionType === 'saveSystemDesignAsTemplate' && args?.description) {
    description = args.description
  } else if (actionType === 'autoDesignEphemeral' && args?.system_templates_json) {
    const templates = args.system_templates_json
    if (Array.isArray(templates) && templates.length > 0) {
      const descriptions = templates.map((t: any) => t?.description).filter((d: any) => d)
      if (descriptions.length > 0) {
        description = descriptions[0]
      }
    }
  }

  return description
}

export const buildTooltipMessage = (
  title: string,
  description: string,
  action: any,
  translate: (key: string, options?: any) => string
): string => {
  let message = description || title
  const args = action?.args || {}

  if (action?.action === 'saveSystemDesignAsTemplate' && args?.name) {
    message += `\n\n${translate('Template')}: ${args.name}`
    if (args?.description) {
      message += `\n\n${args.description}`
    }
  } else if (action?.action === 'autoDesignEphemeral') {
    if (args?.system_templates_json && Array.isArray(args.system_templates_json)) {
      const templates = args.system_templates_json
      const templateNames = templates.map((t: any) => t?.name).filter((n: any) => n)
      if (templateNames.length > 0) {
        const templateLabel = templateNames.length > 1 ? translate('Templates') : translate('Template')
        message += `\n\n${templateLabel}: ${templateNames.join(', ')}`
      }
      const descriptions = templates.map((t: any) => t?.description).filter((d: any) => d)
      if (descriptions.length > 0) {
        message += `\n\n${descriptions[0]}`
      }
    } else if (args?.template_name) {
      message += `\n\n${translate('Template')}: ${args.template_name}`
    }
  }

  return message
}
